import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  BookOpenText,
  PaperPlaneTilt,
  Sparkle,
  ArrowLeft,
  Lock,
  ArrowRight,
  ChatCircle,
  Plus,
  Clock,
  SpeakerHigh,
  SpeakerSlash,
  Microphone,
  Stop,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRESET_TOPICS = [
  { id: "Synonyms", label: "Synonyms", emoji: "🔄", desc: "Words that mean the same" },
  { id: "Antonyms", label: "Antonyms", emoji: "↔️", desc: "Words that mean the opposite" },
  { id: "Homophones", label: "Homophones", emoji: "👯", desc: "Sound-alike words" },
  { id: "Prepositions", label: "Prepositions", emoji: "📍", desc: "On, in, under, beside…" },
  { id: "Verb Tenses", label: "Verb Tenses", emoji: "⏰", desc: "Past, present, future" },
  { id: "Adjectives", label: "Adjectives", emoji: "🎨", desc: "Describing words" },
  { id: "Context Clues", label: "Context Clues", emoji: "🔍", desc: "Guess word meanings" },
  { id: "Sight Words", label: "Sight Words", emoji: "👀", desc: "Common words to know" },
];

const CourseTutor = () => {
  const [hasAccess, setHasAccess] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [email, setEmail] = useState("");

  const [topic, setTopic] = useState(null);
  const [customTopic, setCustomTopic] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [usage, setUsage] = useState({ sessions_today: 0, daily_cap: 3, remaining: 3 });
  const [pastSessions, setPastSessions] = useState([]);
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("tutor_voice_on") === "1");
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);

  const messagesEndRef = useRef(null);
  const audioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  useEffect(() => {
    localStorage.setItem("tutor_voice_on", voiceOn ? "1" : "0");
    if (!voiceOn && audioRef.current) {
      audioRef.current.pause();
    }
  }, [voiceOn]);

  const playTutorVoice = async (text) => {
    if (!voiceOn || !text) return;
    try {
      const res = await axios.post(`${API}/tutor/speak`, { text, voice: "nova" });
      const b64 = res.data.audio_base64;
      if (!b64) return;
      // Stop any prior playback
      if (audioRef.current) {
        audioRef.current.pause();
      }
      const audio = new Audio(`data:audio/mp3;base64,${b64}`);
      audioRef.current = audio;
      audio.play().catch(() => {
        /* browser blocked autoplay — ignore */
      });
    } catch {
      /* silent */
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mime || "audio/webm" });
        await transcribeAndSend(blob);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setIsRecording(true);
    } catch (err) {
      toast.error("Microphone access denied");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAndSend = async (blob) => {
    if (!blob || blob.size < 500) {
      toast.error("Recording too short — try again");
      return;
    }
    setIsTranscribing(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "recording.webm");
      const res = await axios.post(`${API}/tutor/transcribe`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const text = (res.data.text || "").trim();
      if (!text) {
        toast.error("Didn't catch that — try again!");
      } else {
        setInput(text);
        // Auto-send if we have an active session
        if (sessionId) {
          await sendMessageWithText(text);
        }
      }
    } catch {
      toast.error("Voice transcription failed");
    }
    setIsTranscribing(false);
  };

  const sendMessageWithText = async (text) => {
    if (!text.trim() || !sessionId || isSending) return;
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setInput("");
    setIsSending(true);
    try {
      const res = await axios.post(`${API}/tutor/chat`, {
        email,
        topic,
        session_id: sessionId,
        message: text,
      });
      setMessages((prev) => [...prev, { role: "assistant", content: res.data.message }]);
      playTutorVoice(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "The tutor needs a break — try again!");
    }
    setIsSending(false);
  };

  useEffect(() => {
    const stored = localStorage.getItem("course_email");
    if (stored) {
      setEmail(stored);
      checkAccess(stored);
    } else {
      setIsChecking(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const checkAccess = async (emailToCheck) => {
    setIsChecking(true);
    try {
      const res = await axios.post(`${API}/course/access`, { email: emailToCheck });
      if (res.data.has_access) {
        setHasAccess(true);
        localStorage.setItem("course_email", emailToCheck);
        loadUsage(emailToCheck);
        loadPastSessions(emailToCheck);
      } else {
        setHasAccess(false);
      }
    } catch (e) {
      setHasAccess(false);
    }
    setIsChecking(false);
  };

  const loadUsage = async (em) => {
    try {
      const res = await axios.get(`${API}/tutor/usage`, { params: { email: em } });
      setUsage(res.data);
    } catch (e) {
      /* no-op */
    }
  };

  const loadPastSessions = async (em) => {
    try {
      const res = await axios.get(`${API}/tutor/sessions`, { params: { email: em } });
      setPastSessions(res.data || []);
    } catch (e) {
      /* no-op */
    }
  };

  const handleCheckAccess = (e) => {
    e.preventDefault();
    if (email) checkAccess(email);
  };

  const startSession = async (chosenTopic) => {
    if (usage.remaining <= 0) {
      toast.error(`Daily limit reached (${usage.daily_cap} sessions/day). Come back tomorrow! 🌟`);
      return;
    }
    setTopic(chosenTopic);
    setMessages([]);
    setSessionId(null);
    setIsSending(true);
    try {
      const res = await axios.post(`${API}/tutor/chat`, {
        email,
        topic: chosenTopic,
        session_id: null,
        message: null,
      });
      setSessionId(res.data.session_id);
      setMessages([{ role: "assistant", content: res.data.message }]);
      playTutorVoice(res.data.message);
      loadUsage(email);
      loadPastSessions(email);
    } catch (err) {
      const detail = err.response?.data?.detail || "Couldn't start the lesson.";
      toast.error(detail);
      setTopic(null);
    }
    setIsSending(false);
  };

  const resumeSession = async (s) => {
    try {
      const res = await axios.get(`${API}/tutor/session/${s.id}`);
      setTopic(res.data.topic);
      setSessionId(res.data.id);
      setMessages(
        (res.data.messages || []).map((m) => ({ role: m.role, content: m.content }))
      );
    } catch (e) {
      toast.error("Couldn't resume session.");
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || !sessionId || isSending) return;

    const userMsg = input.trim();
    setMessages((prev) => [...prev, { role: "user", content: userMsg }]);
    setInput("");
    setIsSending(true);

    try {
      const res = await axios.post(`${API}/tutor/chat`, {
        email,
        topic,
        session_id: sessionId,
        message: userMsg,
      });
      setMessages((prev) => [...prev, { role: "assistant", content: res.data.message }]);
      playTutorVoice(res.data.message);
    } catch (err) {
      const detail = err.response?.data?.detail || "The tutor needs a break — try again!";
      toast.error(detail);
      // Roll back user msg on error? Keep it for clarity.
    }
    setIsSending(false);
  };

  const endSession = () => {
    setTopic(null);
    setSessionId(null);
    setMessages([]);
    setInput("");
    loadUsage(email);
    loadPastSessions(email);
  };

  // ---------- RENDER ----------
  if (isChecking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="font-nunito text-muted-foreground">Loading your tutor…</p>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-md w-full">
          <div className="bg-white border-2 border-foreground rounded-3xl shadow-brutal p-8">
            <div className="text-center mb-6">
              <Lock size={48} className="text-muted-foreground mx-auto mb-4" />
              <h1 className="font-fredoka font-bold text-2xl text-foreground mb-2">
                AI Tutor — Course Members Only
              </h1>
              <p className="font-nunito text-muted-foreground">
                Enter the email you used to purchase the course.
              </p>
            </div>
            <form onSubmit={handleCheckAccess} className="space-y-4">
              <Input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full font-nunito border-2 border-foreground rounded-xl shadow-brutal-sm py-5"
                data-testid="tutor-access-email-input"
              />
              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary-hover text-white font-nunito font-bold border-2 border-foreground shadow-brutal active-brutal rounded-xl py-5"
                data-testid="tutor-access-submit-btn"
              >
                Unlock AI Tutor
                <ArrowRight size={20} weight="bold" className="ml-2" />
              </Button>
            </form>
            <div className="mt-6 text-center">
              <a href="/course" className="font-nunito font-bold text-primary hover:underline">
                Don't have access? Purchase the Course →
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Active chat
  if (topic && sessionId !== null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex flex-col">
        <nav className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b-2 border-foreground">
          <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkle size={28} weight="fill" className="text-accent-purple" />
              <div>
                <p className="font-fredoka font-bold text-foreground leading-tight">Magic Tutor</p>
                <p className="font-nunito text-xs text-muted-foreground">Topic: {topic}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setVoiceOn((v) => !v)}
                className={`p-2 rounded-full border-2 border-foreground shadow-brutal-sm transition-colors ${
                  voiceOn ? "bg-accent-purple text-white" : "bg-white text-foreground hover:bg-muted"
                }`}
                aria-label={voiceOn ? "Mute tutor voice" : "Unmute tutor voice"}
                data-testid="tutor-voice-toggle"
                title={voiceOn ? "Voice ON — tap to mute" : "Voice OFF — tap to hear tutor"}
              >
                {voiceOn ? <SpeakerHigh size={18} weight="fill" /> : <SpeakerSlash size={18} weight="fill" />}
              </button>
              <Button
                variant="outline"
                onClick={endSession}
                className="font-nunito border-2 border-foreground rounded-full"
                data-testid="tutor-end-session-btn"
              >
                <ArrowLeft size={16} weight="bold" className="mr-2" />
                End Lesson
              </Button>
            </div>
          </div>
        </nav>

        <div className="flex-1 max-w-4xl w-full mx-auto px-4 py-6 overflow-y-auto" data-testid="tutor-chat-window">
          <div className="space-y-4">
            <AnimatePresence initial={false}>
              {messages.map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] px-5 py-3 rounded-2xl border-2 border-foreground shadow-brutal-sm font-nunito text-base whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-primary text-white rounded-br-sm"
                        : "bg-white text-foreground rounded-bl-sm"
                    }`}
                    data-testid={m.role === "assistant" ? "tutor-msg-assistant" : "tutor-msg-user"}
                  >
                    {m.content}
                  </div>
                </motion.div>
              ))}
              {isSending && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
                  <div className="bg-white border-2 border-foreground rounded-2xl rounded-bl-sm px-5 py-3 shadow-brutal-sm">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <div ref={messagesEndRef} />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white/95 backdrop-blur-md border-t-2 border-foreground">
          <form onSubmit={sendMessage} className="max-w-4xl mx-auto px-4 py-3 flex gap-2 items-center">
            <button
              type="button"
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isSending || isTranscribing}
              className={`p-3 rounded-full border-2 border-foreground shadow-brutal-sm shrink-0 transition-colors ${
                isRecording
                  ? "bg-red-500 text-white animate-pulse"
                  : isTranscribing
                  ? "bg-yellow-100 text-yellow-700"
                  : "bg-white text-foreground hover:bg-muted"
              }`}
              aria-label={isRecording ? "Stop recording" : "Start voice message"}
              data-testid="tutor-mic-btn"
              title={isRecording ? "Tap to stop" : "Hold-to-talk: tap to record, tap again to send"}
            >
              {isRecording ? <Stop size={20} weight="fill" /> : <Microphone size={20} weight="fill" />}
            </button>
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={
                isRecording
                  ? "🎙️ Recording — tap the stop button when done…"
                  : isTranscribing
                  ? "✨ Transcribing…"
                  : "Type your answer or tap 🎤 to speak…"
              }
              disabled={isSending || isRecording || isTranscribing}
              className="flex-1 font-nunito border-2 border-foreground rounded-full px-5 py-5"
              data-testid="tutor-input"
            />
            <Button
              type="submit"
              disabled={!input.trim() || isSending || isRecording}
              className="bg-primary hover:bg-primary-hover text-white border-2 border-foreground shadow-brutal active-brutal rounded-full px-6"
              data-testid="tutor-send-btn"
            >
              <PaperPlaneTilt size={20} weight="fill" />
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Topic picker
  return (
    <div className="min-h-screen bg-background">
      <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b-2 border-foreground">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <a href="/course/learn" className="flex items-center gap-2">
            <BookOpenText size={28} weight="duotone" className="text-primary" />
            <span className="font-fredoka font-bold text-lg text-foreground">Back to Course</span>
          </a>
          <div className="flex items-center gap-3">
            <span className="font-nunito text-sm text-muted-foreground hidden sm:inline">{email}</span>
            <div
              className={`flex items-center gap-2 px-3 py-1 rounded-full border-2 border-foreground font-nunito text-sm font-bold ${
                usage.remaining > 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
              }`}
              data-testid="tutor-usage-pill"
            >
              <Clock size={16} weight="fill" />
              {usage.remaining}/{usage.daily_cap} sessions left today
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="text-center mb-10">
          <span className="font-caveat text-2xl text-accent-purple -rotate-2 inline-block mb-2">
            Your personal tutor
          </span>
          <h1 className="font-fredoka font-bold text-4xl sm:text-5xl text-foreground mb-3">
            ✨ Magic <span className="gradient-text">AI Tutor</span>
          </h1>
          <p className="font-nunito text-lg text-muted-foreground max-w-2xl mx-auto">
            Pick a topic and I'll teach you 1-on-1 — at your pace, with kindness and lots of fun.
          </p>
        </div>

        <h2 className="font-fredoka font-bold text-xl text-foreground mb-4">Pick today's topic</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {PRESET_TOPICS.map((t) => (
            <motion.button
              key={t.id}
              whileHover={{ scale: 1.03, rotate: -1 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => startSession(t.id)}
              disabled={isSending || usage.remaining <= 0}
              className="bg-white border-4 border-foreground rounded-2xl shadow-brutal p-5 text-left disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid={`tutor-topic-${t.id.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="text-4xl mb-2">{t.emoji}</div>
              <p className="font-fredoka font-bold text-lg text-foreground">{t.label}</p>
              <p className="font-nunito text-sm text-muted-foreground">{t.desc}</p>
            </motion.button>
          ))}
        </div>

        <div className="bg-white border-2 border-foreground rounded-2xl shadow-brutal p-5 mb-10">
          <h3 className="font-fredoka font-bold text-foreground mb-3">Or type your own topic</h3>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (customTopic.trim()) startSession(customTopic.trim());
            }}
            className="flex gap-2"
          >
            <Input
              value={customTopic}
              onChange={(e) => setCustomTopic(e.target.value)}
              placeholder="e.g., Compound Words"
              className="flex-1 font-nunito border-2 border-foreground rounded-xl"
              maxLength={60}
              data-testid="tutor-custom-topic-input"
            />
            <Button
              type="submit"
              disabled={!customTopic.trim() || usage.remaining <= 0}
              className="bg-accent-purple hover:bg-purple-600 text-white font-nunito font-bold border-2 border-foreground shadow-brutal active-brutal rounded-xl"
              data-testid="tutor-start-custom-btn"
            >
              <Plus size={18} weight="bold" className="mr-1" />
              Start
            </Button>
          </form>
        </div>

        {pastSessions.length > 0 && (
          <div>
            <h2 className="font-fredoka font-bold text-xl text-foreground mb-4 flex items-center gap-2">
              <ChatCircle size={22} weight="duotone" /> Continue a past lesson
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {pastSessions.slice(0, 6).map((s) => (
                <button
                  key={s.id}
                  onClick={() => resumeSession(s)}
                  className="bg-white border-2 border-foreground rounded-xl shadow-brutal-sm p-4 text-left hover:bg-muted/30 transition-colors"
                  data-testid="tutor-past-session"
                >
                  <p className="font-fredoka font-bold text-foreground">{s.topic}</p>
                  <p className="font-nunito text-xs text-muted-foreground">
                    {s.message_count} messages • {new Date(s.last_message_at).toLocaleDateString()}
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}

        {usage.remaining <= 0 && (
          <div className="mt-8 bg-yellow-50 border-2 border-yellow-400 rounded-2xl p-5 text-center">
            <p className="font-fredoka font-bold text-lg text-foreground mb-1">
              🌟 You did all {usage.daily_cap} sessions today!
            </p>
            <p className="font-nunito text-muted-foreground">
              Come back tomorrow for more 1-on-1 tutoring.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseTutor;
